import * as React from 'react';
import { Label, makeStyles, mergeClasses, shorthands, Text, tokens } from '@fluentui/react-components';
import { defineMessage, useIntl } from 'react-intl';
import type { ConceptStageEnum } from '@foundershub/api-client';
import {
  BuildDefinitionIcon,
  CityNextIcon,
  DesignIcon,
  FinancialSolidIcon,
  FormFieldDescription,
  ProductIcon,
  TextLink,
} from '@foundershub/components';
import { conceptStageLabels } from '../modules/intl/metadata';

interface Props {
  error?: string;
  label?: string;
  onChange: (value: ConceptStageEnum) => void;
  required?: boolean;
  selected: ConceptStageEnum | null;
  stages: Array<ConceptStageEnum>;
}

export function StageSelector({ error, label, onChange, required = false, selected, stages }: Props) {
  const intl = useIntl();
  const id = React.useId();
  const classes = useStyles();

  return (
    <div>
      <Label id={id} required={required}>
        {label || intl.formatMessage(fieldStage)}
      </Label>
      <div
        aria-describedby={`desc-${id}`}
        aria-errormessage={error ? `error-${id}` : undefined}
        aria-required
        role="radiogroup"
        aria-labelledby={id}
        className={classes.choicegroup}
      >
        {stages.map((slug) => {
          const Icon = stageToIcon[slug];
          const checked = selected === slug;
          return (
            <label
              key={slug}
              className={mergeClasses(
                classes.choice,
                !!error && classes.choiceError,
                checked && classes.choiceSelected
              )}
            >
              <input
                type="radio"
                aria-describedby={`desc-${id}`}
                checked={checked}
                className={classes.radio}
                onChange={(event) => event.target.checked && onChange(slug)}
              />

              <Icon className={classes.icon} />
              <Text className={classes.choicelabel}>{intl.formatMessage(conceptStageLabels[slug])}</Text>
            </label>
          );
        })}
      </div>
      <FormFieldDescription
        hint={
          <TextLink openNewTab href="https://foundershubsupportcenter.powerappsportals.com/article/KA-01137/en-us">
            {intl.formatMessage(howToPickLabel)}
          </TextLink>
        }
        validationMessage={error}
        validationState={error ? 'error' : undefined}
        id={id}
      />
    </div>
  );
}

const stageToIcon: Record<string, typeof BuildDefinitionIcon> = {
  'concept-design': DesignIcon,
  prototype: ProductIcon,
  'building-mvp': BuildDefinitionIcon,
  'mvp-in-market': CityNextIcon,
  'established-market': FinancialSolidIcon,
};

const fieldStage = defineMessage({
  defaultMessage: 'What development stage best describes your startup or project?',
  description: 'choice select label',
});

const howToPickLabel = defineMessage({
  defaultMessage: 'How to pick your stage.',
  description: 'link to FAQs for business/development stage',
});

const useStyles = makeStyles({
  choicegroup: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    ...shorthands.gap(tokens.spacingHorizontalM),
  },
  choice: {
    flexBasis: '18%',
    flexShrink: 1,
    flexGrow: 1,
    maxWidth: '33%',
    display: 'flex',
    boxSizing: 'border-box',
    flexDirection: 'column',
    position: 'relative',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'pointer',
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalM, tokens.spacingVerticalM),
    ':hover': {
      ...shorthands.borderColor(tokens.colorNeutralStroke1Hover),
      color: tokens.colorNeutralForeground1,
    },
  },
  choiceSelected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    color: tokens.colorBrandForeground2,
    ':hover': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      color: tokens.colorBrandForeground2,
    },
  },
  choiceError: {
    ...shorthands.borderColor(tokens.colorPaletteRedForeground1),
  },
  radio: {
    opacity: '0',
    position: 'absolute',
    ...shorthands.inset(0, 0, 0, 0),
  },
  icon: {
    width: '36px',
    height: '36px',
  },
  choicelabel: {
    display: 'flex',
    textAlign: 'center',
  },
});
